import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetAssignmentsQuery, useGetCustomersQuery, useGetEmployeesQuery } from "../../../api/plamo-api";
import { CheckCircleFilled } from "@ant-design/icons";
import jquery from "jquery";
import styled from "styled-components";
import { Collapse, Tree, TreeDataNode, TreeProps } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchBar from "../search-bar";
import { IconCircleUserSolid, IconHouseSolid } from "../../../icon";

const getGoogleMaps = (): any => {
  const GoogleMaps = (window as any).google?.maps;
  if (!GoogleMaps) {
    throw new Error('Google Maps API not loaded. Make sure REACT_APP_GOOGLE_MAPS_API_KEY is set.');
  }
  return GoogleMaps;
};

const waitForGoogleMaps = (): Promise<any> => {
  const win = window as any;
  if (win.__googleMapsLoaded) {
    return win.__googleMapsLoaded.then(() => getGoogleMaps());
  }
  // Fallback if script was already loaded
  try {
    return Promise.resolve(getGoogleMaps());
  } catch {
    return Promise.reject(new Error('Google Maps API not configured'));
  }
};

const htmlToElement = (html: string): HTMLElement => jquery(html).get(0);

const toLegacyMapEvent = (map: any) => ({
  target: {
    getCenter: () => { const c = map.getCenter(); return { lat: c.lat(), lng: c.lng() }; },
    getZoom: () => map.getZoom(),
  }
});

const MarkerIcon = styled.div`
  font-size: 15px;
  display: inline-block;
  padding: 0.1em;
  width: 30px;
  height: 30px;
  text-align: center;
  color: white;
  border-radius: 15px;
  position: absolute;
`

const createMarker = (map: any, position: [number, number], divIcon: HTMLElement | undefined, opacity: number, popupContent: HTMLElement | undefined, callbacks: any, onClick: any) => {
  let content;
  if(divIcon) {
    content = divIcon.cloneNode(true) as HTMLElement;
    content.style.transform = 'translate(0, 50%)';
    content.style.paddingTop = '0.3em';
    content.style.opacity = String(opacity);
  }

  const GoogleMaps = getGoogleMaps();
  const marker = new GoogleMaps.marker.AdvancedMarkerElement({ position: { lat: position[0], lng: position[1] }, content, map });

  const listeners: any[] = [];
  if(onClick) {
    listeners.push(marker.addListener("click", onClick));
  }

  let infoWindow;
  if(popupContent) {
    const elem = jquery(popupContent.cloneNode(true));
    for(const key in callbacks) {
      const btn = elem.find(`.${key}`);
      btn.click(callbacks[key]);
    }
    infoWindow = new GoogleMaps.InfoWindow({ content: elem.get(0) });
  }

  return {
    marker,
    infoWindow,
    listeners,
    openPopup: (open: boolean) => {
      if(open && infoWindow) {
        infoWindow.open({ anchor: marker, map });
      } else {
        infoWindow?.close();
      }
    },
    destroy: () => {
      listeners.forEach(l => l.remove());
      infoWindow?.close();
      marker.map = null;
    }
  };
}

const createRoutePopupElement = (description: string, distance: number, duration: number): HTMLElement => {
  const div = document.createElement('div');
  div.innerHTML = `
    <h3>${description}</h3>
    <p>
      Entfernung ${`${Math.round(distance / 100) / 10}`.replace('.', ',')} km<br/>
      Dauer ${Math.round(duration / 60)} min
    </p>
  `;
  return div;
}

const createRoute = (map: any, route: any) => {
  const GoogleMaps = getGoogleMaps();
  const polyline = new GoogleMaps.Polyline({ path: route.path, strokeColor: 'red', strokeWeight: 5, map });

  const popupContent = createRoutePopupElement(route.description, route.distance, route.duration);
  const infoWindow = new GoogleMaps.InfoWindow({ content: popupContent, position: route.path[Math.floor(route.path.length / 2)] });
  infoWindow.open({ map });

  return {
    polyline,
    infoWindow,
    destroy: () => {
      polyline.setMap(null);
      infoWindow.close();
    }
  };
}

const LayerSelector = ({ selectedLayers, setSelectedLayers }) => {
  const checkedKeys: string[] = useMemo(() => Object.entries(selectedLayers).filter(([_, v]) => v).map(([k, _]) => k) || [], [selectedLayers]);

  const treeData: TreeDataNode[] = [
    {
      title: 'Mitarbeiter',
      key: 'employees',
      children: [
        {
          title: 'inaktiv ja/nein',
          key: 'employeesInactiveSelect',
          children: [
            {
              title: 'aktiv',
              key: 'employeesActive'
            },
            {
              title: 'inaktiv',
              key: 'employeesInactive'
            }
          ]
        },
        {
          title: 'Kapazitäten ja/nein',
          key: 'employeesCapacitiesSelect',
          children: [
            {
              title: 'mit Kapazitäten',
              key: 'employeesWithCapacities',
            },
            {
              title: 'ohne Kapazitäten',
              key: 'employeesWithoutCapacities',
            },
          ]
        }
      ],
    },
    {
      title: "Kunden",
      key: "customers",
      children: [
        {
          title: 'inaktiv ja/nein',
          key: 'customersInactiveSelect',
          children: [
            {
              title: 'aktiv',
              key: 'customersActive'
            },
            {
              title: 'inaktiv',
              key: 'customersInactive'
            }
          ]
        },
        {
          title: "zugeordnet ja/nein",
          key: 'customerAssignedSelect',
          children: [
            {
              title: 'zugeordnet',
              key: 'customersWithAssignment',
            },
            {
              title: 'nicht zugeordnet',
              key: 'customersWithoutAssignment',
            },
          ]
        }
      ]
    }
  ];

  const onSelect: TreeProps['onSelect'] = (selectedKeys, info) => {
    console.log('selected', selectedKeys, info);
  };

  const onCheck: TreeProps['onCheck'] = useCallback((checkedKeys) => {
    const selectedLayers: SelectedLayers = {};
    if(checkedKeys["checked"]) {
      checkedKeys["checked"].forEach(k => selectedLayers[k] = true);
    } else {
      (checkedKeys as any).forEach(k => selectedLayers[k] = true);
    }
    console.log("Setting selected layers", selectedLayers);
    setSelectedLayers(selectedLayers)
  }, [setSelectedLayers]);

  return (
    <Tree
      checkable
      defaultExpandedKeys={['0-0-0', '0-0-1']}
      checkedKeys={checkedKeys}
      defaultCheckedKeys={['0-0-0', '0-0-1']}
      onSelect={onSelect}
      onCheck={onCheck}
      treeData={treeData}
    />
  );
}

const LayerContainer = styled.div`
  position: absolute;
  left: 1em;
  bottom: 1em;
  background-color: white;
  border-radius: 8px;
  z-index: 1000;
`

interface LatLon {
  lat: number;
  lon: number;
  searchText: string;
}


interface SelectedLayers {
  employeesInactive?: boolean;
  employeesActive?: boolean;
  employeesWithCapacities?: boolean;
  employeesWithoutCapacities?: boolean;
  customersActive?: boolean;
  customersInactive?: boolean;
  customersWithAssignment?: boolean;
  customersWithoutAssignment?: boolean;
}

export default function Map({ search, onZoomEnd, onMoveEnd, initialCenter, initialZoom, center, zoom}: any) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate();
  const mapContainerRef = useRef<any>();
  const markerIconCustomerRef = useRef<any>();
  const markerIconSelectedCustomerRef = useRef<any>();
  const markerIconAssignedCustomerRef = useRef<any>();
  const markerIconEmployeeRef = useRef<any>();
  const markerIconSelectedEmployeeRef = useRef<any>();
  const markerIconAssignedEmployeeRef = useRef<any>();
  const employeeMarkersRef = useRef<any>({});
  const customerMarkersRef = useRef<any>({});
  const selectedAddressMarkerRef = useRef<any>(null);
  const routeRef = useRef<any>(null);
  const [map, setMap] = useState();
  const [selectedAddress, setSelectedAddress] = useState<LatLon | null>(null);
  const [calculatedRoute, setCalculatedRoute] = useState<any>(null);
  const [selectedLayers, setSelectedLayers] = useState<SelectedLayers>({ employeesActive: true, employeesWithCapacities: true, employeesWithoutCapacities: true, customersActive: true, customersWithAssignment: true, customersWithoutAssignment: true })

  const { data: employees } = useGetEmployeesQuery({}) as any;
  const { data: customers } = useGetCustomersQuery({}) as any;
  const { data: assignments } = useGetAssignmentsQuery({}) as any;

  const selectedEmployeeId = parseInt(searchParams.get("selectedEmployeeId") || '');
  const selectedEmployee = useMemo(() => employees?.items?.find(e => e.id === selectedEmployeeId), [employees?.items, selectedEmployeeId]);
  const setSelectedEmployee = useCallback(e => {
    setSearchParams(prevUrlSearchParams => {
      if(e) {
        prevUrlSearchParams.set("selectedEmployeeId", e.id);
      } else {
        prevUrlSearchParams.delete("selectedEmployeeId");
      }
      return prevUrlSearchParams;
    });
  }, [setSearchParams])

  const selectedCustomerId = parseInt(searchParams.get("selectedCustomerId") || '');
  const selectedCustomer = useMemo(() => customers?.items?.find(c => c.id === selectedCustomerId), [customers?.items, selectedCustomerId]);
  const setSelectedCustomer = useCallback(c => {
    setSearchParams(prevUrlSearchParams => {
      if(c) {
        prevUrlSearchParams.set("selectedCustomerId", c.id);
      } else {
        prevUrlSearchParams.delete("selectedCustomerId");
      }
      return prevUrlSearchParams;
    });
  }, [setSearchParams])

  useEffect(() => {
    waitForGoogleMaps().then(() => {
      const refElem = mapContainerRef.current;
      const center = initialCenter?.lat && initialCenter?.lng ? { lat: initialCenter.lat, lng: initialCenter.lng } : { lat: 48.1372039, lng: 11.6478594 }
      const zoom = initialZoom ?? 13
      const GoogleMaps = getGoogleMaps();
      const map = new GoogleMaps.Map(refElem, {
        center,
        zoom,
        maxZoom: 19,
        mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID',
      });

      setMap(map);
    }).catch(err => {
      console.error('Failed to load Google Maps:', err);
    });
  }, [mapContainerRef, setMap, initialCenter?.lat, initialCenter?.lng, initialZoom])

  useEffect(() => {
    if(onZoomEnd && map) {
      const listener = (map as any).addListener("zoom_changed", () => onZoomEnd(toLegacyMapEvent(map)));
      return () => listener.remove();
    }
  }, [map, onZoomEnd]);

  useEffect(() => {
    if(onMoveEnd && map) {
      const listener = (map as any).addListener("idle", () => onMoveEnd(toLegacyMapEvent(map)));
      return () => listener.remove();
    }
  }, [map, onMoveEnd]);

  useEffect(() => {
    if(center?.lat && center?.lng && zoom) {
      (map as any)?.setCenter({ lat: center.lat, lng: center.lng });
      (map as any)?.setZoom(zoom);
    }
  }, [map, center?.lat, center?.lng, zoom])

  useEffect(() => {
    if(selectedCustomer) {
      (map as any)?.panTo({ lat: selectedCustomer.lat, lng: selectedCustomer.lon })
    }
  }, [selectedCustomer, map])

  useEffect(() => {
    if(selectedEmployee) {
      (map as any)?.panTo({ lat: selectedEmployee.lat, lng: selectedEmployee.lon })
    }
  }, [selectedEmployee, map])

  const onSearch = useCallback(async (searchObj) => {
    if(searchObj.type === 'employee') {
      setSelectedEmployee(searchObj);
      (map as any)?.setZoom(17);
      (map as any)?.panTo({ lat: searchObj.lat, lng: searchObj.lon });
    } else if(searchObj.type === 'customer') {
      setSelectedCustomer(searchObj);
      (map as any)?.setZoom(17);
      (map as any)?.panTo({ lat: searchObj.lat, lng: searchObj.lon });
    } else {
      let selectedAddress: LatLon | null = null;
      try {
        const GoogleMaps = getGoogleMaps();
        const { results } = await new GoogleMaps.Geocoder().geocode({ address: searchObj.address });
        if(results.length > 0) {
          const location = results[0].geometry.location;
          selectedAddress = { lat: location.lat(), lon: location.lng(), searchText: searchObj.address };
          (map as any)?.setZoom(17);
          (map as any)?.panTo({ lat: selectedAddress.lat, lng: selectedAddress.lon });
        }
      } catch (e) {
        console.log("Geocoding failed", e);
      }
      setSelectedAddress(selectedAddress);
    }

  }, [map, setSelectedAddress, setSelectedEmployee, setSelectedCustomer]);

  const onShowRoute = useCallback(async (description, a, b) => {
    try {
      const GoogleMaps = getGoogleMaps();
      const result = await new GoogleMaps.DirectionsService().route({
        origin: { lat: a[0], lng: a[1] },
        destination: { lat: b[0], lng: b[1] },
        travelMode: GoogleMaps.TravelMode.DRIVING,
      });
      const route = result.routes[0];
      if(route) {
        const leg = route.legs[0];
        setCalculatedRoute({ description, distance: leg.distance.value, duration: leg.duration.value, path: route.overview_path });
      }
    } catch (e) {
      console.log("Directions request failed", e);
    }
  }, [setCalculatedRoute]);

  const visibleEmployees = useMemo(() => (employees?.items || [])
      .filter(e => e.lat && e.lon)
      .filter(e => (selectedLayers.employeesActive && e.active) || (selectedLayers.employeesInactive && !e.active))
      .filter(e => ((!e.capacity || e.capacity === 'y') && selectedLayers.employeesWithCapacities) || (e.capacity === 'n' && selectedLayers.employeesWithoutCapacities)),
      [employees?.items, selectedLayers]);

  const visibleCustomers = useMemo(() => (customers?.items || [])
      .filter(e => e.lat && e.lon)
      .filter(e => (selectedLayers.customersActive && e.status === 'Aktiv') || (selectedLayers.customersInactive && e.status !== 'Aktiv'))
      .filter(e => (selectedLayers.customersWithAssignment && !!assignments?.items?.find(a => a.customerId === e.id)) || (selectedLayers.customersWithoutAssignment && !assignments?.items?.find(a => a.customerId === e.id)))
      ,
      [customers?.items, selectedLayers, assignments?.items]);

  // Manage employee markers imperatively
  useEffect(() => {
    if(!map) return;

    const markerMap = employeeMarkersRef.current;
    const visibleIds = new Set(visibleEmployees.map(e => e.id));
    const existingIds = Object.keys(markerMap).map(Number);

    // Remove markers that are no longer visible
    for(const id of existingIds) {
      if(!visibleIds.has(id)) {
        markerMap[id]?.destroy();
        delete markerMap[id];
      }
    }

    // Add or update markers
    for(const e of visibleEmployees) {
      const assigned = selectedCustomer && !selectedEmployee && assignments?.items?.find(a => a.customerId === selectedCustomer.id && a.employeeId === e.id);
      const isSelected = e.id === selectedEmployee?.id;
      const iconHtml = assigned ? markerIconAssignedEmployeeRef.current?.outerHTML : (isSelected ? markerIconSelectedEmployeeRef.current?.outerHTML : markerIconEmployeeRef.current?.outerHTML);

      let markerData = markerMap[e.id];
      if(!markerData) {
        // Create new marker
        const popupDiv = document.createElement('div');
        popupDiv.innerHTML = `
          <div>
            <h3>${e.firstName} ${e.lastName} <button class="linkEmployee" type="button">👉</button></h3>
            <p>
              ${e.addressStreet} ${e.addressHouseNumber}<br />
              ${e.addressZipCode} ${e.addressCity}<br/>
            </p>
            <table>
              <tr><td>Auto:</td><td>${e.car === 'y' ? 'Ja' : (e.car === 'n' ? 'Nein' : 'k.A.')}</td></tr>
              <tr><td>Allergien:</td><td>${e.allergies ?? 'k.A.'}</td></tr>
              <tr><td>Phobien:</td><td>${e.animalPhobias ?? 'k.A.'}</td></tr>
            </table>
            <div>
              ${selectedCustomer && !selectedEmployee ? `<button class="showRouteCustomer" type="button" style="width: 100%">Route zum Kunden</button>` : ''}
              ${selectedAddress && !selectedEmployee ? `<button class="showRouteAddress" type="button" style="width: 100%">Route zur Adresse</button>` : ''}
            </div>
          </div>
        `;

        markerData = createMarker(map, [e.lat, e.lon], htmlToElement(iconHtml), 1, popupDiv, {
          linkEmployee: () => navigate(`/employee/${e.id}`),
          showRouteCustomer: () => onShowRoute(`Strecke ${e.firstName} ${e.lastName} - ${selectedCustomer.firstName} ${selectedCustomer.lastName}`, [e.lat, e.lon], [selectedCustomer.lat, selectedCustomer.lon]),
          showRouteAddress: () => onShowRoute(`Strecke ${e.firstName} ${e.lastName} - ${selectedAddress?.searchText}`, [e.lat, e.lon], [selectedAddress?.lat, selectedAddress?.lon])
        }, () => e.id === selectedEmployee?.id ? setSelectedEmployee(null) : setSelectedEmployee(e));

        markerMap[e.id] = markerData;
      } else {
        // Update icon if changed
        const currentMarker = markerData.marker;
        if(currentMarker.content?.outerHTML !== iconHtml) {
          const content = htmlToElement(iconHtml);
          content.style.transform = 'translate(0, 50%)';
          currentMarker.content = content;
        }
      }

      // Update popup open state
      markerData.openPopup(isSelected);
    }

    return () => {
      Object.values(markerMap).forEach((m: any) => m.destroy());
      for(const key in markerMap) delete markerMap[key];
    };
  }, [map, visibleEmployees, selectedEmployee, selectedCustomer, selectedAddress, assignments?.items, navigate, onShowRoute, setSelectedEmployee, setSelectedCustomer]);

  // Manage customer markers imperatively
  useEffect(() => {
    if(!map) return;

    const markerMap = customerMarkersRef.current;
    const visibleIds = new Set(visibleCustomers.map(c => c.id));
    const existingIds = Object.keys(markerMap).map(Number);

    // Remove markers that are no longer visible
    for(const id of existingIds) {
      if(!visibleIds.has(id)) {
        markerMap[id]?.destroy();
        delete markerMap[id];
      }
    }

    // Add or update markers
    for(const c of visibleCustomers) {
      const assigned = selectedEmployee && !selectedCustomer && assignments?.items?.find(a => a.employeeId === selectedEmployee.id && a.customerId === c.id);
      const isSelected = c.id === selectedCustomer?.id;
      const iconHtml = assigned ? markerIconAssignedCustomerRef.current?.outerHTML : (isSelected ? markerIconSelectedCustomerRef.current?.outerHTML : markerIconCustomerRef.current?.outerHTML);

      let markerData = markerMap[c.id];
      if(!markerData) {
        // Create new marker
        const popupDiv = document.createElement('div');
        popupDiv.innerHTML = `
          <div>
            <h3>${c.firstName} ${c.lastName}<button class="linkCustomer" type="button">👉</button></h3>
            <p>
              ${c.addressStreet} ${c.addressHouseNumber}<br />
              ${c.addressZipCode} ${c.addressCity}<br/>
            </p>
            <div>
              ${selectedEmployee && !selectedCustomer ? `<button class="showRouteEmployee" type="button" style="width: 100%">Route zum Mitarbeiter</button>` : ''}
              ${selectedAddress && !selectedCustomer ? `<button class="showRouteAddress" type="button" style="width: 100%">Route zur Adresse</button>` : ''}
            </div>
          </div>
        `;

        markerData = createMarker(map, [c.lat, c.lon], htmlToElement(iconHtml), 1, popupDiv, {
          linkCustomer: () => navigate(`/customer/${c.id}`),
          showRouteEmployee: () => onShowRoute(`Strecke ${selectedEmployee.firstName} ${selectedEmployee.lastName} - ${c.firstName} ${c.lastName}`, [selectedEmployee.lat, selectedEmployee.lon], [c.lat, c.lon]),
          showRouteAddress: () => onShowRoute(`Strecke ${selectedAddress?.searchText} - ${c.firstName} ${c.lastName}`, [c.lat, c.lon], [selectedAddress?.lat, selectedAddress?.lon]),
        }, () => c.id === selectedCustomer?.id ? setSelectedCustomer(null) : setSelectedCustomer(c));

        markerMap[c.id] = markerData;
      } else {
        // Update icon if changed
        const currentMarker = markerData.marker;
        if(currentMarker.content?.outerHTML !== iconHtml) {
          const content = htmlToElement(iconHtml);
          content.style.transform = 'translate(0, 50%)';
          currentMarker.content = content;
        }
      }

      // Update popup open state
      markerData.openPopup(isSelected);
    }

    return () => {
      Object.values(markerMap).forEach((m: any) => m.destroy());
      for(const key in markerMap) delete markerMap[key];
    };
  }, [map, visibleCustomers, selectedCustomer, selectedEmployee, selectedAddress, assignments?.items, navigate, onShowRoute, setSelectedCustomer]);

  // Manage selected address marker imperatively
  useEffect(() => {
    if(!map || !selectedAddress) {
      selectedAddressMarkerRef.current?.destroy();
      selectedAddressMarkerRef.current = null;
      return;
    }

    const popupDiv = document.createElement('div');
    popupDiv.innerHTML = `<h3>${selectedAddress.searchText}</h3>`;

    selectedAddressMarkerRef.current?.destroy();
    selectedAddressMarkerRef.current = createMarker(map, [selectedAddress.lat, selectedAddress.lon], undefined, 1, popupDiv, {}, undefined);
    selectedAddressMarkerRef.current.openPopup(true);

    return () => {
      selectedAddressMarkerRef.current?.destroy();
      selectedAddressMarkerRef.current = null;
    };
  }, [map, selectedAddress]);

  // Manage route imperatively
  useEffect(() => {
    if(!map || !calculatedRoute) {
      routeRef.current?.destroy();
      routeRef.current = null;
      return;
    }

    routeRef.current?.destroy();
    routeRef.current = createRoute(map, calculatedRoute);

    return () => {
      routeRef.current?.destroy();
      routeRef.current = null;
    };
  }, [map, calculatedRoute]);

  return <div style={{ width: '100%', height: '100%', display: "flex", flexDirection: "column", position: "relative" }}>
    <MarkerIcon ref={markerIconCustomerRef} style={{ background: "green" }}><IconHouseSolid /></MarkerIcon>
    <MarkerIcon ref={markerIconSelectedCustomerRef} style={{ background: "green", boxShadow: 'inset 0px 0px 20px 12px rgba(255,255,255,0.7)' }}><IconHouseSolid /></MarkerIcon>
    <MarkerIcon ref={markerIconAssignedCustomerRef} style={{ background: "green" }}><IconHouseSolid /><CheckCircleFilled style={{ position: "absolute", color: "#8F8", fontSize: "16px",  left: "20px" }} /></MarkerIcon>
    <MarkerIcon ref={markerIconEmployeeRef} style={{ background: "violet" }}><IconCircleUserSolid /></MarkerIcon>
    <MarkerIcon ref={markerIconSelectedEmployeeRef} style={{ background: "violet", boxShadow: 'inset 0px 0px 20px 12px rgba(255,255,255,0.7)' }}><IconCircleUserSolid /></MarkerIcon>
    <MarkerIcon ref={markerIconAssignedEmployeeRef} style={{ background: "violet" }}><IconCircleUserSolid /><CheckCircleFilled style={{ position: "absolute", color: "#8F8", fontSize: "16px",  left: "20px" }} /></MarkerIcon>
    <div ref={mapContainerRef} style={{ width: "100%", flex: 1 }} />
    <LayerContainer>
      <Collapse items={[{key: 1, label: "Layer", children: <LayerSelector selectedLayers={selectedLayers} setSelectedLayers={setSelectedLayers} /> }]} defaultActiveKey={['1']} />
    </LayerContainer>
    { search &&
      <SearchBar allowAddressSearch onSearch={onSearch} employees={employees?.items} customers={customers?.items} />
    }
  </div>
}
